import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/propertyViewingsEdit.module.css';
import propertyViewingsService from '../services/propertyViewingsService';

const PropertyViewingsEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [viewing, setViewing] = useState(null);
  const [property, setProperty] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    property_id: '',
    user_id: '',
    attendees: '',
    rooms_taken: '',
    day: '',
    time_from: '',
    time_to: '',
    notes: '',
    guardian_id: '',
    guardians_attendees: '',
    guardians_taken: ''
  });
  
  // Guardian management
  const [guardianSearch, setGuardianSearch] = useState('');
  const [selectedGuardians, setSelectedGuardians] = useState([]);
  const [guardianSearchResults, setGuardianSearchResults] = useState([]);
  const [guardianSearchTimeout, setGuardianSearchTimeout] = useState(null);

  // Refs for date/time pickers
  const dayInputRef = useRef(null);
  const timeFromInputRef = useRef(null);
  const timeToInputRef = useRef(null);


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Live database API calls only
      const viewingResponse = await propertyViewingsService.editPropertyViewing(id);
      const data = viewingResponse.data.data; // Extract nested data structure
      
      setViewing(data.viewing);
      setProperty(data.property);
      
      // Convert users object to array format for dropdown
      // Backend returns users as object: { 1: "First Last", 2: "First Last", ... } (Laravel format)
      // Laravel: ['Please select'] + $users where $users is { id: "Full Name" }
      let usersArray = [];
      if (data.users && typeof data.users === 'object') {
        // Handle both object format { 1: "Name" } and array format [{ id: 1, name: "Name" }]
        if (Array.isArray(data.users)) {
          usersArray = data.users;
        } else {
          // Convert Laravel object format { id: "Full Name" } to array [{ id, full_name }]
          usersArray = Object.entries(data.users)
            .map(([id, fullName]) => ({
              id: parseInt(id),
              full_name: fullName || ''
            }))
            .sort((a, b) => {
              // Sort by full name to match Laravel display order
              return a.full_name.localeCompare(b.full_name);
            });
        }
      }
      
      setUsers(usersArray);
      
      // Set form data
      setFormData({
        property_id: data.viewing.property_id,
        user_id: data.viewing.user_id,
        attendees: data.viewing.attendees,
        rooms_taken: data.viewing.rooms_taken,
        day: data.viewing.day,
        time_from: data.viewing.time_from,
        time_to: data.viewing.time_to,
        notes: data.viewing.notes,
        guardian_id: '',
        guardians_attendees: data.viewing.guardians_attendees,
        guardians_taken: data.viewing.guardians_taken
      });
      
      // Set selected guardians from confirmations if available
      if (data.viewing && data.viewing.confirmations && data.viewing.confirmations.length > 0) {
        setSelectedGuardians(data.viewing.confirmations
          .filter(c => c.guardian && c.guardian.user) // Only include guardians with valid user data
          .map(c => c.guardian)
        );
      } else {
        setSelectedGuardians([]);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load property viewing data from database.');
      
      // Fallback to mock data for testing when API fails
      const mockViewing = {
        id: parseInt(id) || 1,
        property_id: 1,
        user_id: 1,
        attendees: '2',
        rooms_taken: '2',
        day: '17/02/2025',
        time_from: '10:50',
        time_to: '11:30',
        notes: 'Sample viewing notes',
        guardians_attendees: 'Guardian 1, Guardian 2',
        guardians_taken: '1',
        confirmations: [
          {
            id: 1,
            guardian: {
              id: 1,
              unique_guardian_number: 'G001',
              user: { fullName: 'Sakina Walters' }
            },
            status: 'going',
            statusName: 'going'
          }
        ],
        property: {
          id: 1,
          name: '30 Bowie Close',
          postcode: 'SW4 8MA'
        },
        user: {
          id: 1,
          full_name: 'John Manager'
        }
      };
      
      const mockUsers = [
        { id: 1, full_name: 'John Manager', email: 'john@example.com' },
        { id: 2, full_name: 'Jane Manager', email: 'jane@example.com' },
        { id: 3, full_name: 'Mike Supervisor', email: 'mike@example.com' }
      ];
      
      setViewing(mockViewing);
      setProperty(mockViewing.property);
      setUsers(mockUsers);
      
      setFormData({
        property_id: mockViewing.property_id,
        user_id: mockViewing.user_id,
        attendees: mockViewing.attendees,
        rooms_taken: mockViewing.rooms_taken,
        day: mockViewing.day,
        time_from: mockViewing.time_from,
        time_to: mockViewing.time_to,
        notes: mockViewing.notes,
        guardian_id: '',
        guardians_attendees: mockViewing.guardians_attendees,
        guardians_taken: mockViewing.guardians_taken
      });
      
      if (mockViewing.confirmations && mockViewing.confirmations.length > 0) {
        setSelectedGuardians(mockViewing.confirmations.map(c => c.guardian));
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Load data
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // loadData is memoized with useCallback and depends on id, so we only need id here

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (guardianSearchTimeout) {
        clearTimeout(guardianSearchTimeout);
      }
    };
  }, [guardianSearchTimeout]);

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle guardian search with debouncing
  const handleGuardianSearch = async (e) => {
    const searchTerm = e.target.value;
    setGuardianSearch(searchTerm);
    
    // Clear existing timeout
    if (guardianSearchTimeout) {
      clearTimeout(guardianSearchTimeout);
    }
    
    if (searchTerm.length >= 2) {
      // Debounce API call - wait 300ms after user stops typing
      const timeout = setTimeout(async () => {
        try {
          // Call real API for guardian search
          const response = await propertyViewingsService.searchGuardians(searchTerm);
          
          // Handle different response formats
          let guardians = [];
          if (response.data && Array.isArray(response.data)) {
            guardians = response.data;
          } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
            guardians = response.data.data;
          } else if (response.data && response.data.success && response.data.data) {
            guardians = response.data.data;
          }
          
          // Map guardians to expected format
          const mappedGuardians = guardians.map(guardian => {
            // Handle different guardian data structures
            if (guardian.user) {
              return {
                id: guardian.id,
                name: `${guardian.user.first_name || ''} ${guardian.user.last_name || ''}`.trim() || guardian.user.fullName || 'Unknown Guardian',
                email: guardian.user.email || '',
                unique_guardian_number: guardian.unique_guardian_number || ''
              };
            } else {
              return {
                id: guardian.id,
                name: guardian.name || guardian.fullName || `${guardian.first_name || ''} ${guardian.last_name || ''}`.trim() || 'Unknown Guardian',
                email: guardian.email || '',
                unique_guardian_number: guardian.unique_guardian_number || ''
              };
            }
          });
          
          setGuardianSearchResults(mappedGuardians);
        } catch (error) {
          console.error('Error searching guardians:', error);
          setGuardianSearchResults([]);
        }
      }, 300);
      
      setGuardianSearchTimeout(timeout);
    } else {
      setGuardianSearchResults([]);
    }
  };

  // Convert date format for date input (mm/dd/yyyy to yyyy-mm-dd)
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return '';
    // Already in yyyy-mm-dd format
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    // Handle mm/dd/yyyy format (Laravel format)
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        // Check if first part is month (1-12) or day (1-31)
        const firstPart = parseInt(parts[0], 10);
        if (firstPart > 12) {
          // dd/mm/yyyy format
          const [day, month, year] = parts;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        } else {
          // mm/dd/yyyy format
          const [month, day, year] = parts;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
    }
    return '';
  };

  // Convert date format from date input (yyyy-mm-dd to mm/dd/yyyy)
  const convertFromDateInputFormat = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${month}/${day}/${year}`;
    }
    return dateString;
  };

  // Handle date change from native date picker
  const handleDateChange = (e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const formattedDate = convertFromDateInputFormat(dateValue);
      handleFormChange('day', formattedDate);
    } else {
      handleFormChange('day', '');
    }
  };

  // Handle time change from native time picker
  const handleTimeChange = (field, e) => {
    const timeValue = e.target.value;
    handleFormChange(field, timeValue || '');
  };

  // Open date picker
  const openDatePicker = (e) => {
    e?.preventDefault();
    if (dayInputRef.current) {
      setTimeout(() => {
        try {
          if (dayInputRef.current.showPicker) {
            dayInputRef.current.showPicker();
          } else {
            dayInputRef.current.click();
          }
        } catch (error) {
          dayInputRef.current.click();
        }
      }, 0);
    }
  };

  // Open time picker
  const openTimePicker = (ref, e) => {
    e?.preventDefault();
    if (ref.current) {
      setTimeout(() => {
        try {
          if (ref.current.showPicker) {
            ref.current.showPicker();
          } else {
            ref.current.click();
          }
        } catch (error) {
          ref.current.click();
        }
      }, 0);
    }
  };

  // Add guardian to viewing
  const handleAddGuardian = (guardian) => {
    // Check if guardian is already selected
    if (!selectedGuardians.find(g => g.id === guardian.id)) {
      // Ensure guardian has proper structure with user object for display
      const guardianToAdd = {
        ...guardian,
        user: guardian.user || {
          fullName: guardian.name || 'Unknown Guardian',
          email: guardian.email || ''
        }
      };
      
      setSelectedGuardians(prev => [...prev, guardianToAdd]);
      setGuardianSearch('');
      setGuardianSearchResults([]);
    } else {
      setGuardianSearch('');
      setGuardianSearchResults([]);
    }
  };

  // Remove guardian from viewing
  const handleRemoveGuardian = (guardianId) => {
    setSelectedGuardians(prev => prev.filter(g => g.id !== guardianId));
  };

  // Handle confirmation status change
  const handleConfirmationStatusChange = async (confirmationId, newStatus) => {
    try {
      // Convert string status to integer ID (matching Laravel config)
      const statusMapping = {
        'going': 0,
        'attended': 1,
        'not_attended': 2
      };
      
      const statusId = statusMapping[newStatus];
      if (statusId === undefined) {
        throw new Error(`Invalid status: ${newStatus}`);
      }
      
      // Live database API call only
      await propertyViewingsService.changePropertyViewingConfirmationStatus(confirmationId, statusId);
      toast.success('Confirmation status updated successfully');
      loadData(); // Reload data from database
    } catch (error) {
      console.error('Error updating confirmation status:', error);
      toast.error('Failed to update confirmation status in database');
    }
  };


  // Laravel-style form validation
  const validateForm = () => {
    const errors = {};

    // Required fields (matching Laravel UpdatePropertyViewingRequest)
    if (!formData.property_id) {
      errors.property_id = 'Property is required';
    }
    if (!formData.user_id) {
      errors.user_id = 'User is required';
    }
    if (!formData.day) {
      errors.day = 'Day is required';
    }
    if (!formData.time_from) {
      errors.time_from = 'Time from is required';
    }
    // Notes field is optional - no validation needed

    // Format validation
    if (formData.day && !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(formData.day)) {
      errors.day = 'Day must be in dd/mm/yyyy format';
    }
    if (formData.time_from && !/^\d{1,2}:\d{2}$/.test(formData.time_from)) {
      errors.time_from = 'Time from must be in HH:MM format';
    }
    if (formData.time_from && /^\d{1,2}:\d{2}$/.test(formData.time_from)) {
      const [h] = formData.time_from.split(':').map(Number);
      if (h < 9) {
        errors.time_from = 'Viewings cannot start before 09:00';
      }
    }
    if (formData.time_to && !/^\d{1,2}:\d{2}$/.test(formData.time_to)) {
      errors.time_to = 'Time to must be in HH:MM format';
    }

    // Numeric validation - only for INTEGER fields
    // Laravel: 'attendees' => 'integer', 'rooms_taken' => 'integer'
    // Note: guardians_taken and guardians_attendees are TEXT fields (for names), NOT numbers
    if (formData.attendees && formData.attendees !== '' && !Number.isInteger(Number(formData.attendees))) {
      errors.attendees = 'Attendees must be a number';
    }
    if (formData.rooms_taken && formData.rooms_taken !== '' && !Number.isInteger(Number(formData.rooms_taken))) {
      errors.rooms_taken = 'Rooms taken must be a number';
    }
    // guardians_taken is a TEXT field for guardian names - NO numeric validation needed

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Client-side validation
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      // Display validation errors
      Object.values(validationErrors).forEach(error => {
        toast.error(error);
      });
      setSubmitting(false);
      return;
    }
    
    try {
      const submitData = {
        ...formData,
        guardians: selectedGuardians.map(g => g.id)
      };

      // Live database API call only
      await propertyViewingsService.updatePropertyViewing(id, submitData);
      toast.success('Property viewing updated successfully');
      navigate(`/properties/${formData.property_id}/viewings`);
    } catch (error) {
      console.error('Error updating viewing:', error);
      
      // Handle Laravel validation errors
      if (error.response && error.response.status === 400 && error.response.data.errors) {
        const serverErrors = error.response.data.errors;
        Object.values(serverErrors).forEach(errorMsg => {
          toast.error(errorMsg);
        });
      } else {
        toast.error('Failed to update property viewing. Please check your input and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Dashboard', path: '/' },
    { name: 'Property Viewings', path: '/property_viewings' },
    { name: property?.name || 'Property', path: `/properties/${property?.id}` },
    { name: 'Edit', path: `/property_viewings/${id}/edit` }
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          Loading property viewing...
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>

      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((crumb, index) => (
          <span key={index}>
            {index > 0 && <span className={styles.breadcrumbSeparator}>/</span>}
            {index === breadcrumbs.length - 1 ? (
              <span className={styles.breadcrumbItem}>{crumb.name}</span>
            ) : (
              <span
                className={styles.breadcrumbItem}
                onClick={() => navigate(crumb.path)}
                style={{ cursor: 'pointer', color: '#337ab7' }}
              >
                {crumb.name}
              </span>
            )}
          </span>
        ))}
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
            <div className={styles.panelHeading}>
              <h3 className={styles.panelTitle}>Edit Property Viewing</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <fieldset className={styles.formHorizontal}>
                  {/* Property */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Property</label>
                    <div className={styles.colSm5}>
                      <input type="hidden" value={formData.property_id} />
                      <div className={styles.asInput}>{property?.name}</div>
                    </div>
                  </div>

                  {/* Postcode */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}></label>
                    <div className={styles.colSm5}>
                      <div className={styles.asInput}>{property?.postcode}</div>
                    </div>
                  </div>

                  {/* User */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.formLabel} ${styles.labelRequired}`}>User</label>
                    <div className={styles.colSm5}>
                      <select
                        className={styles.formControl}
                        required
                        value={formData.user_id}
                        onChange={(e) => handleFormChange('user_id', e.target.value)}
                      >
                        <option value="">Please select</option>
                        {Array.isArray(users) ? users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.full_name}
                          </option>
                        )) : (
                          <option value="">No users available</option>
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Attendees */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Attendees</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        value={formData.attendees}
                        onChange={(e) => handleFormChange('attendees', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Rooms Taken */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Rooms Taken</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        value={formData.rooms_taken}
                        onChange={(e) => handleFormChange('rooms_taken', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Day */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.formLabel} ${styles.labelRequired}`}>Day</label>
                    <div className={styles.colSm5}>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className={styles.formControl}
                          required
                          value={formData.day}
                          onChange={(e) => handleFormChange('day', e.target.value)}
                          placeholder="mm/dd/yyyy"
                          onClick={openDatePicker}
                          readOnly
                          style={{ paddingRight: '35px', cursor: 'pointer' }}
                        />
                        {/* Invisible date input overlay */}
                        <input
                          ref={dayInputRef}
                          type="date"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: 'pointer',
                            zIndex: 10,
                            fontSize: '16px',
                            padding: '8px 35px 8px 12px',
                            margin: 0,
                            border: 'none',
                            background: 'transparent',
                            boxSizing: 'border-box'
                          }}
                          value={convertToDateInputFormat(formData.day)}
                          onChange={handleDateChange}
                        />
                        <span
                          className="glyphicon glyphicon-calendar"
                          onClick={openDatePicker}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#999',
                            cursor: 'pointer',
                            pointerEvents: 'auto',
                            zIndex: 11
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Time From */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.formLabel} ${styles.labelRequired}`}>Time From</label>
                    <div className={styles.colSm5}>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className={styles.formControl}
                          required
                          value={formData.time_from}
                          onChange={(e) => handleFormChange('time_from', e.target.value)}
                          placeholder="--:--"
                          onClick={(e) => openTimePicker(timeFromInputRef, e)}
                          readOnly
                          style={{ paddingRight: '35px', cursor: 'pointer' }}
                        />
                        {/* Invisible time input overlay */}
                        <input
                          ref={timeFromInputRef}
                          type="time"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: 'pointer',
                            zIndex: 10,
                            fontSize: '16px',
                            padding: '8px 35px 8px 12px',
                            margin: 0,
                            border: 'none',
                            background: 'transparent',
                            boxSizing: 'border-box'
                          }}
                          value={formData.time_from}
                          onChange={(e) => handleTimeChange('time_from', e)}
                        />
                        <span
                          className="glyphicon glyphicon-time"
                          onClick={(e) => openTimePicker(timeFromInputRef, e)}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#999',
                            cursor: 'pointer',
                            pointerEvents: 'auto',
                            zIndex: 11
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Time To */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.formLabel} ${styles.labelRequired}`}>Time To</label>
                    <div className={styles.colSm5}>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className={styles.formControl}
                          required
                          value={formData.time_to}
                          onChange={(e) => handleFormChange('time_to', e.target.value)}
                          placeholder="--:--"
                          onClick={(e) => openTimePicker(timeToInputRef, e)}
                          readOnly
                          style={{ paddingRight: '35px', cursor: 'pointer' }}
                        />
                        {/* Invisible time input overlay */}
                        <input
                          ref={timeToInputRef}
                          type="time"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: 'pointer',
                            zIndex: 10,
                            fontSize: '16px',
                            padding: '8px 35px 8px 12px',
                            margin: 0,
                            border: 'none',
                            background: 'transparent',
                            boxSizing: 'border-box'
                          }}
                          value={formData.time_to}
                          onChange={(e) => handleTimeChange('time_to', e)}
                        />
                        <span
                          className="glyphicon glyphicon-time"
                          onClick={(e) => openTimePicker(timeToInputRef, e)}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#999',
                            cursor: 'pointer',
                            pointerEvents: 'auto',
                            zIndex: 11
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Notes</label>
                    <div className={styles.colSm5}>
                      <textarea
                        className={styles.formControl}
                        rows="4"
                        value={formData.notes}
                        onChange={(e) => handleFormChange('notes', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Guardians Section */}
                  <div className={styles.reportSectionHeader}>Guardians</div>
                  
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Guardians confirmed</label>
                    <div className={styles.colSm5} style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className={styles.formControl}
                        placeholder="Search guardians..."
                        value={guardianSearch}
                        onChange={handleGuardianSearch}
                        autoComplete="off"
                      />
                      
                      {/* Guardian Typeahead Dropdown */}
                      {guardianSearch.length >= 2 && (
                        <div 
                          style={{ 
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 10000,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderTop: 'none',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            marginTop: '2px',
                            width: '100%'
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {guardianSearchResults.length > 0 ? (
                            guardianSearchResults.map((guardian) => {
                              const displayName = guardian.name || 'Unknown Guardian';
                              const displayEmail = guardian.email || 'No email';
                              const displayUGD = guardian.unique_guardian_number || '';
                              
                              return (
                                <div
                                  key={guardian.id || `guardian-${guardian.unique_guardian_number || Math.random()}`}
                                  onClick={() => {
                                    handleAddGuardian(guardian);
                                  }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    padding: '8px 12px',
                                    borderBottom: '1px solid #eee',
                                    backgroundColor: 'white'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                  }}
                                >
                                  <strong>{displayName}</strong> {displayEmail && `(${displayEmail})`} {displayUGD && `[${displayUGD}]`}
                                </div>
                              );
                            })
                          ) : (
                            <div 
                              style={{ 
                                padding: '10px', 
                                color: '#666', 
                                fontStyle: 'italic',
                                textAlign: 'center'
                              }}
                            >
                              No guardians found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Selected Guardians */}
                  {selectedGuardians.length > 0 && (
                    <div className={styles.guardiansSection}>
                      <div className={styles.reportSectionHeader}>Selected Guardians</div>
                      <div className={styles.guardiansList}>
                        {selectedGuardians.map((guardian, index) => (
                          <div key={guardian.id} className={styles.guardianRow}>
                            <div className={styles.guardianCol}>
                              <label className={styles.guardianLabel}>
                                {guardian.user?.fullName || guardian.name || 'Unknown Guardian'}
                              </label>
                            </div>
                            <div className={styles.guardianRemoveCol}>
                              <button
                                type="button"
                                className={styles.btnRemove}
                                onClick={() => handleRemoveGuardian(guardian.id)}
                              >
                                Delete
                              </button>
                            </div>
                            <input 
                              type="hidden" 
                              name={`guardians[${index}][guardianId]`} 
                              value={guardian.id} 
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Guardians Attendees */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Guardians Attendees</label>
                    <div className={styles.colSm5}>
                      <textarea
                        className={styles.formControl}
                        rows="3"
                        value={formData.guardians_attendees}
                        onChange={(e) => handleFormChange('guardians_attendees', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Guardians Taken */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Guardians Taken</label>
                    <div className={styles.colSm5}>
                      <textarea
                        className={styles.formControl}
                        rows="3"
                        value={formData.guardians_taken}
                        onChange={(e) => handleFormChange('guardians_taken', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className={styles.formGroup}>
                    <div className={styles.colSm3}></div>
                    <div className={styles.colSm5}>
                      <button
                        type="submit"
                        className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <div className={styles.loadingSpinner}></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className={styles.glyphicon}></i> Save Viewing
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>

              {/* Confirmations Section */}
              <div className={styles.row}>
                <div className={styles.colXsOffset3}>
                  <strong>Confirmations:</strong>

                  {viewing?.confirmations && viewing.confirmations.length > 0 ? (
                    <table className={`${styles.table} ${styles.tableHover} ${styles.mt20}`}>
                      <thead>
                        <tr>
                          <th>Guardian</th>
                          <th>Status</th>
                          <th>Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewing.confirmations.map(confirmation => (
                          <tr key={confirmation.id}>
                            <td>
                              {confirmation.guardian && confirmation.guardian.user ? (
                                <a 
                                  href={`/guardians/${confirmation.guardian.id}`}
                                  className={styles.guardianLink}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/guardians/${confirmation.guardian.id}`);
                                  }}
                                >
                                  {confirmation.guardian.user.fullName}
                                </a>
                              ) : (
                                <span className={styles.noData}>No guardian data</span>
                              )}
                            </td>
                            <td>{confirmation.statusName}</td>
                            <td className={styles.textCenter}>
                              {confirmation.status !== 'going' && (
                                <button
                                  className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary} ${styles.hiddenPrintLink}`}
                                  onClick={() => handleConfirmationStatusChange(confirmation.id, 'going')}
                                >
                                  Going
                                </button>
                              )}

                              {confirmation.status !== 'attended' && (
                                <button
                                  className={`${styles.btn} ${styles.btnSm} ${styles.btnSuccess} ${styles.hiddenPrintLink}`}
                                  onClick={() => handleConfirmationStatusChange(confirmation.id, 'attended')}
                                >
                                  Attended
                                </button>
                              )}

                              {confirmation.status !== 'not_attended' && (
                                <button
                                  className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger} ${styles.hiddenPrintLink}`}
                                  onClick={() => handleConfirmationStatusChange(confirmation.id, 'not_attended')}
                                >
                                  Not Attended
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p>There is no confirmed guardian!</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyViewingsEdit; 